/**
 * Servicio para gestionar solicitudes de mesonero en tiempo real.
 * Los consumidores crean documentos en waiterRequests y este servicio
 * los escucha via onSnapshot para mostrarlos en el dashboard.
 */

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  limit,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Suscribirse a solicitudes activas (pending + acknowledged) en tiempo real
 * @param {string} restaurantId
 * @param {function} callback - (requests: WaiterRequest[]) => void
 * @returns {function} unsubscribe
 */
export const subscribeToWaiterRequests = (restaurantId, callback) => {
  if (!restaurantId) {
    console.error('subscribeToWaiterRequests: restaurantId es requerido');
    callback([]);
    return () => {};
  }

  const requestsRef = collection(db, 'restaurants', restaurantId, 'waiterRequests');
  const q = query(
    requestsRef,
    where('status', 'in', ['pending', 'acknowledged']),
    orderBy('createdAt', 'desc'),
    limit(30)
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    callback(requests);
  }, (error) => {
    console.error('Error listening to waiter requests:', error);
    callback([]);
  });
};

/**
 * Suscribirse SOLO a las solicitudes asignadas a un garzón (mesas que atiende).
 * Para staff que no es admin/owner: cada garzón (y cajero/host) ve únicamente las
 * solicitudes de las mesas que tiene asignadas. El backend estampa
 * `assignedWaiterId` (= uid del garzón) en cada solicitud vía Cloud Function.
 * @param {string} restaurantId
 * @param {string} waiterUid - uid del garzón logueado (== assignedWaiterId)
 * @param {function} callback - (requests: WaiterRequest[]) => void
 * @returns {function} unsubscribe
 */
export const subscribeToWaiterRequestsForWaiter = (restaurantId, waiterUid, callback) => {
  if (!restaurantId || !waiterUid) {
    console.error('subscribeToWaiterRequestsForWaiter: restaurantId y waiterUid son requeridos');
    callback([]);
    return () => {};
  }

  const requestsRef = collection(db, 'restaurants', restaurantId, 'waiterRequests');
  const q = query(
    requestsRef,
    where('assignedWaiterId', '==', waiterUid),
    where('status', 'in', ['pending', 'acknowledged']),
    orderBy('createdAt', 'desc'),
    limit(30)
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    callback(requests);
  }, (error) => {
    console.error('Error listening to waiter requests (scoped):', error);
    callback([]);
  });
};

/**
 * Marcar solicitud como "acknowledged" (en atención)
 */
export const acknowledgeRequest = async (restaurantId, requestId, staffName) => {
  const ref = doc(db, 'restaurants', restaurantId, 'waiterRequests', requestId);
  await updateDoc(ref, {
    status: 'acknowledged',
    acknowledgedAt: serverTimestamp(),
    acknowledgedBy: staffName || 'Staff',
  });
};

/**
 * Marcar solicitud como "resolved" y programar TTL a 24h
 */
export const resolveRequest = async (restaurantId, requestId) => {
  const ref = doc(db, 'restaurants', restaurantId, 'waiterRequests', requestId);
  const expireAt = Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
  await updateDoc(ref, {
    status: 'resolved',
    resolvedAt: serverTimestamp(),
    expireAt,
  });
};

/**
 * Obtener historial de solicitudes recientes (resolved/cancelled) para el dropdown.
 * Si se pasa `waiterUid`, filtra solo las asignadas a ese garzón (mismo criterio
 * que la suscripción activa: admin/owner ven todo, el resto solo lo suyo).
 */
export const getRequestHistory = async (restaurantId, maxResults = 20, waiterUid = null) => {
  if (!restaurantId) return [];

  const requestsRef = collection(db, 'restaurants', restaurantId, 'waiterRequests');
  const constraints = [where('status', 'in', ['resolved', 'cancelled'])];
  if (waiterUid) {
    constraints.push(where('assignedWaiterId', '==', waiterUid));
  }
  constraints.push(orderBy('createdAt', 'desc'), limit(maxResults));

  const snapshot = await getDocs(query(requestsRef, ...constraints));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};
